// Copyright ©️ 2026 eVolve MEP, LLC

import type { ComponentProps, FC } from 'react';

import type { WorkOrderPaginated } from '@evolvemep/api-ts';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { ApproveWorkOrderButton } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/ApproveWorkOrderButton';
import { ReleaseWorkOrderButton } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/ReleaseWorkOrderButton';
import { SubmitWorkOrderButton } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/SubmitWorkOrderButton';
import type { WorkOrderAction } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import type { WorkOrderActionButtonsProps } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/WorkOrderActionButtons';
import type { WorkOrderModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/modalRegistries';

/** Props shared by every secondary action's modal, opened through `useWorkOrderModals`. */
export type WorkOrderActionModalProps = ModalRegistryManagedProps & {
  workOrder: WorkOrderPaginated;
  onWorkOrderActioned: () => void;
};

type SecondaryActionProps = Pick<WorkOrderActionModalProps, 'workOrder' | 'onWorkOrderActioned'>;

/** Registered modals that declare every prop a secondary action passes, and need no others. */
type SecondaryActionModal = {
  [K in keyof WorkOrderModals]: SecondaryActionProps extends Omit<
    ComponentProps<WorkOrderModals[K]>,
    'opened' | 'onClose' | 'keepMounted'
  >
    ? keyof SecondaryActionProps extends keyof ComponentProps<WorkOrderModals[K]>
      ? K
      : never
    : never;
}[keyof WorkOrderModals];

/**
 * A single work order action. `primary` actions render as a dedicated prominent button that owns its own
 * logic. Every other action is purely "open a modal": it carries the `label` and the registry key of its
 * `modal`, and both surfaces render a light button / menu item that opens that modal.
 */
export type WorkOrderActionDef =
  | {
      action: WorkOrderAction;
      primary: true;
      Component: FC<WorkOrderActionButtonsProps>;
    }
  | {
      action: WorkOrderAction;
      primary: false;
      /** Button/menu label */
      label: string;
      /** Non-primary actions are expected to open a Modal to perform the action */
      modal: SecondaryActionModal;
    };

/**
 * Single source of truth for the set of work order actions and their metadata.
 * Use {@link canEditWorkOrder} to determine availability.
 *
 * It is expected that at most one Primary action is available at a time.
 *
 * Order matters.
 * For the desktop button row, later entries render further to the right.
 * For the mobile menu, later entries render further down in the menu.
 */
export const WORK_ORDER_ACTIONS: WorkOrderActionDef[] = [
  /* region Secondary actions */
  { action: 'transfer', primary: false, label: 'Transfer', modal: 'transferWorkOrderModal' },
  { action: 'cancel', primary: false, label: 'Cancel', modal: 'cancelWorkOrderModal' },
  { action: 'requestCancel', primary: false, label: 'Request cancellation', modal: 'cancelWorkOrderModal' },
  { action: 'reject', primary: false, label: 'Reject', modal: 'rejectWorkOrderModal' },
  { action: 'block', primary: false, label: 'Block work order', modal: 'blockWorkOrderModal' },
  /* endregion */

  /* region Primary actions */
  { action: 'approve', primary: true, Component: ApproveWorkOrderButton },
  { action: 'submit', primary: true, Component: SubmitWorkOrderButton },
  { action: 'release', primary: true, Component: ReleaseWorkOrderButton },
  /* endregion */
];
